import dayjs from 'dayjs';
import dayjsPluginUTC from 'dayjs-plugin-utc';
import _ from 'lodash';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import {
  GetPassengerTypes,
  PassengerInfoFormErrors,
  PassengerInfoFormErrorsWhenPastPassenger,
  PassengerInfoValidations,
  PassengerTypes,
  SecurityInformationTypes,
  TravelTypes,
  ValidAgeForPrimaryPassenger,
  ValidAgeForPrimaryPassengerFlightOnly,
} from '../../constants';
import calculateAge from '../calculateAge';
import validateEmail from './validateEmail';
import validatePhoneNumberWithCountrySelect from './validatePhoneNumberWithCountrySelect';
import validateDocuments from './validateDocuments';
import diagnoseNameFailure, { buildNameFailureMessage } from './diagnoseNameFailure';
import getFlightSegmentsExcludingHiddenStops from '../getFlightSegmentsExcludingHiddenStops';
import getTripItineraries from '../getTripItineraries';
import checkDoShowSavePassenger from '../checkDoShowSavePassenger';

dayjs.extend(customParseFormat);
dayjs.extend(dayjsPluginUTC);

const setEmptyFieldValidation = (_emptyFieldValidations, roomIndex, passengerIndex, fieldName) => {
  const validations = _emptyFieldValidations.validations || {};
  validations[roomIndex] = validations[roomIndex] || {};
  validations[roomIndex][passengerIndex] = validations[roomIndex][passengerIndex] || {};
  validations[roomIndex][passengerIndex] = {
    ...validations[roomIndex][passengerIndex],
    invalidFields: validations[roomIndex][passengerIndex].invalidFields || [],
  };
  validations[roomIndex][passengerIndex].invalidFields.push(fieldName);
  return {
    ..._emptyFieldValidations,
    validations,
  };
};

const getMissingSeatsMessage = (missingSeats, flightSegments) =>
  (missingSeats || [])
    .map((flightNum) => {
      const flight = flightSegments && flightSegments.find((f) => f.flightNumber === flightNum);
      return flight
        ? `${flight.carrierCode} ${flightNum} (${flight.originAirportCode} - ${flight.destinationAirportCode})`
        : `#${flightNum}`;
    })
    .join(', ');

const validatePassengerSeats = (
  passenger,
  passengersByRoom,
  passengersFormValidations,
  flightSegments,
) => {
  try {
    const passengerPreferences = passenger.travelerPreferences.flightSeatNumbers || {};
    const isLapInfant = passenger.isLapInfant || false;
    const updatedFormValidations = { ...passengersFormValidations };

    const isValid = passengersByRoom.every((otherPassenger) => {
      if (otherPassenger.passengerIndex === passenger.passengerIndex) return true;
      const otherPassengerFlightSeats = otherPassenger.travelerPreferences.flightSeatNumbers || {};
      const seatKeys = Object.keys(otherPassengerFlightSeats);
      const missingSeats = seatKeys.filter(
        (seatKey) => otherPassengerFlightSeats[seatKey] && !passengerPreferences[seatKey],
      );
      if (missingSeats.length > 0 && !isLapInfant) {
        updatedFormValidations[passenger.passengerIndex].seatValidate =
          `Please select a seat for passenger ${passenger.passengerIndex + 1} on flight ${getMissingSeatsMessage(missingSeats, flightSegments)}`;
        return false;
      }
      return true;
    });
    return isValid;
  } catch {
    return true;
  }
};

const validatePassengerUniqueness = (passengersArray, travelAgency, loggedInUserEmail) => {
  const passengerNames = passengersArray
    .map((passenger) =>
      [passenger.firstName, passenger.middleName, passenger.lastName].join('').toLowerCase(),
    )
    .filter((item) => item);

  const uniqueNames = new Set(passengerNames);
  const hasDuplicateNames = uniqueNames.size < passengerNames.length;

  let errorMessage = '';
  let hasEmailViolation = false;
  const allowAgentsCreateClientsByPersonInfo =
    travelAgency && travelAgency.doAllowAgentsCreateClientsByPersonInfo;
  const safePassengersArray = Array.isArray(passengersArray)
    ? _.map(passengersArray, (p) => ({
        ...p,
        doSaveCustomerInfo: checkDoShowSavePassenger(
          passengersArray,
          p,
          p.passengerIndex,
          false,
          !!loggedInUserEmail,
          travelAgency,
          loggedInUserEmail,
        )
          ? p.doSaveCustomerInfo
          : false,
      }))
    : [];
  const passengersWithCustomerId = _.filter(
    safePassengersArray,
    (f) => f && f.customerId && f.customerId.length,
  );

  if (_.find(_.countBy(passengersWithCustomerId, 'customerId'), (c) => c > 1)) {
    // MULTIPLE PASSENGERS WITH THE SAME CUSTOMER PROFILE VIOLATION
    hasEmailViolation = true;
    errorMessage = 'Multiple passengers with the same customer profile are not allowed.';
  } else if (!allowAgentsCreateClientsByPersonInfo) {
    // WHEN AGENTS ARE NOT ALLOWED TO CREATE CLIENTS BY PERSON INFO,
    // MULTIPLE PASSENGERS WITH THE SAME EMAIL VIOLATION
    const passengersWithValidEmails = _.filter(
      safePassengersArray,
      (f) => f && f.doSaveCustomerInfo && f.contactEmail && f.contactEmail.trim(),
    );
    hasEmailViolation = _.find(_.countBy(passengersWithValidEmails, 'contactEmail'), (c) => c > 1);
    if (hasEmailViolation) {
      errorMessage =
        'Multiple passengers cannot use the same email address when "Save customer profile" is selected. Please uncheck "Save customer profile" for at least one passenger with the same email address or provide different email addresses.';
    }
  } else {
    const passengersWithEmails = _.filter(
      safePassengersArray,
      (f) => f && f.doSaveCustomerInfo && f.contactEmail && f.contactEmail.trim(),
    );

    if (passengersWithEmails && passengersWithEmails.length > 0) {
      const emailCounts = _.countBy(
        passengersWithEmails
          .filter((p) => !loggedInUserEmail || p.contactEmail.trim() !== loggedInUserEmail)
          .map((p) => p.contactEmail.trim()),
      );
      const hasDuplicateNonLoggedInEmails = Object.values(emailCounts).some((count) => count >= 2);

      if (hasDuplicateNonLoggedInEmails) {
        errorMessage =
          'Multiple passengers cannot use the same email address when "Save customer profile" is selected. Please uncheck "Save customer profile" for at least one passenger with the same email address or provide different email addresses.';
        hasEmailViolation = true;
      } else {
        const passengersWithLoggedInUserEmail = loggedInUserEmail
          ? passengersWithEmails.filter(
              (p) => p && p.contactEmail && p.contactEmail.trim() === loggedInUserEmail,
            )
          : [];

        if (passengersWithLoggedInUserEmail && passengersWithLoggedInUserEmail.length >= 2) {
          const loggedInUserNames = passengersWithLoggedInUserEmail
            .map((passenger) => {
              if (!passenger) return '';
              const firstName =
                passenger.firstName && passenger.firstName.trim() ? passenger.firstName.trim() : '';
              const middleName =
                passenger.middleName && passenger.middleName.trim()
                  ? passenger.middleName.trim()
                  : '';
              const lastName =
                passenger.lastName && passenger.lastName.trim() ? passenger.lastName.trim() : '';
              const fullName = [firstName, middleName, lastName].join('').toLowerCase();
              return fullName;
            })
            .filter((name) => name);
          if (loggedInUserNames.length >= 2) {
            const uniqueLoggedInUserNames = new Set(loggedInUserNames);
            hasEmailViolation = uniqueLoggedInUserNames.size < loggedInUserNames.length;
          }
        }
      }
    }
  }
  return { isInvalid: hasDuplicateNames || hasEmailViolation, errorMessage };
};

const buildFlightMismatchMessage = (passengerTypeLabel, flight) => {
  return `${passengerTypeLabel.replace(/\s/g, '-')} fare is not available for the ${flight.originAirportCode}-${flight.destinationAirportCode} flight because the traveler does not meet the age requirements for the entire trip. Please book the ${flight.originAirportCode}-${flight.destinationAirportCode} flight separately.`;
};

const validatePassengers = (
  passengers,
  doHoldOnlyCruiseAsOption,
  travelAgency,
  tripConfirmation,
  doIgnoreEmptyPassengerDetailValidation,
  isLoggedIn,
  loggedInUserEmail,
  itinerariesToBook,
  trip,
  isInsuranceSelected,
) => {
  let isValid = true;
  const flightsGroup = getFlightSegmentsExcludingHiddenStops(trip.flightItinerary);
  const flightSegments = Object.values(flightsGroup).flat();
  const allFlights =
    trip.flightItinerary?.flightSegments?.flatMap((segment) => segment.flights) || [];
  const isInternationalFlight =
    trip && trip.flightItinerary && trip.flightItinerary.isInternationalFlight;
  let similarPassengersValidation = null;
  let emptyPassengerDetailValidation = {};
  const passengersFormValidations = Object.values(passengers).map((passenger) => ({
    ...passenger.formValidations,
  }));
  const passengersArray = Object.values(passengers);
  _.each(
    _.groupBy(Object.values(passengers), (p) => p.roomIndex),
    (passengersByRoom) => {
      passengersByRoom.forEach((passenger, index) => {
        const pastPassengerNumber = passenger.isPastPassenger
          ? passenger.pastPassengerNumber
          : null;
        const passengerInfoValidations = PassengerInfoValidations({
          pastPassengerNumber,
          doHoldOnlyCruiseAsOption,
          passengerIndexByRoom: index,
          travelAgency,
          isLoggedIn,
          itinerariesToBook,
          doUseTravelerInfoAsEmergencyContact:
            passenger.doUseTravelerInfoAsEmergencyContact &&
            !passenger.doUsePrimaryTravelerAsEmergencyContact,
          isInsuranceSelected,
        });

        Object.keys(passengerInfoValidations).forEach((key) => {
          // REQUIRED FIELD VALIDATION
          if (key === 'stateProvCode' && !passenger[key]) {
            if (passenger.countryCode === 'US' && isInsuranceSelected) {
              passengersFormValidations[passenger.passengerIndex][key] = 'State is required';
              isValid = false;
              return;
            }
          } else if (passengerInfoValidations[key] && !passenger[key]) {
            passengersFormValidations[passenger.passengerIndex][key] =
              doHoldOnlyCruiseAsOption &&
              pastPassengerNumber &&
              _.find(Object.keys(PassengerInfoFormErrorsWhenPastPassenger), (k) => k === key)
                ? PassengerInfoFormErrorsWhenPastPassenger[key]
                : PassengerInfoFormErrors[key];
            isValid = false;
            return;
          }

          // CHECK OTHER VALIDATIONS
          if (key === 'firstName' || key === 'middleName' || key === 'lastName') {
            const value = passenger[key];
            // const matched = value ? value.match(/^([a-z]+[ ]?|[a-z]+['-]?)+$/gi) : true;
            // const matched = value ? value.match(/^([a-z]+[ ]?|[a-z]+)+$/gi) : true;
            const matched = value ? value.match(/^[a-z]+([ -][a-z]+)*$/i) : true;

            if (!matched) {
              passengersFormValidations[passenger.passengerIndex][key] =
                buildNameFailureMessage(diagnoseNameFailure(value)) ||
                'The name field can only contain letters, spaces, or hyphens (and must not end with a trailing space or hyphen).';
              isValid = false;
            }
            if (matched && key !== 'middleName' && value && value.length < 2) {
              passengersFormValidations[passenger.passengerIndex][key] =
                'The name entered is invalid, minmum 2 characters required';
              isValid = false;
            }
          }
          if (!passengerInfoValidations[key] && !passenger[key]) return;
          const isFlightOnlyTrip =
            _.findIndex(getTripItineraries(trip), (i) => i.mode !== TravelTypes.flight) === -1;
          const tripModes = getTripItineraries(trip).map((i) => i.mode);
          const isTourOnlyTrip =
            tripModes.length > 0 && tripModes.every((mode) => mode === TravelTypes.tour);

          // VALIDATE PRIMARY PASSENGER AGE
          const minAgeForPrimaryPassenger = isFlightOnlyTrip
            ? ValidAgeForPrimaryPassengerFlightOnly
            : ValidAgeForPrimaryPassenger;
          if (key === 'birthDate' && passenger.isPrimaryContact && !isTourOnlyTrip) {
            if (allFlights?.length > 0) {
              const passengerTypeLabel =
                PassengerTypes[passenger.passengerType]?.text || passenger.passengerType || 'Adult';

              allFlights.every((flight, flightIndex) => {
                const age = calculateAge(passenger.birthDate, flight.departDateTime);
                if (age < minAgeForPrimaryPassenger) {
                  let errorOnThisFlight = '';
                  if (
                    flightIndex > 0 &&
                    !passengersFormValidations[passenger.passengerIndex][key]
                  ) {
                    errorOnThisFlight = buildFlightMismatchMessage(passengerTypeLabel, flight);
                  }
                  passengersFormValidations[passenger.passengerIndex][key] = isFlightOnlyTrip
                    ? `Primary passenger should be at least ${ValidAgeForPrimaryPassengerFlightOnly} years old at the time of travel. ${errorOnThisFlight}`.trim()
                    : `Primary passenger should be an adult (${ValidAgeForPrimaryPassenger} years old at the time of travel. ${errorOnThisFlight})`.trim();
                  isValid = false;
                  return false;
                }
                return true;
              });
            } else {
              const age = calculateAge(passenger.birthDate);
              if (age < minAgeForPrimaryPassenger) {
                passengersFormValidations[passenger.passengerIndex][key] = isFlightOnlyTrip
                  ? `Primary passenger should be at least ${ValidAgeForPrimaryPassengerFlightOnly} years old.`
                  : `Primary passenger should be an adult (${ValidAgeForPrimaryPassenger} years old.)`;
                isValid = false;
              }
            }
          }
          if (
            key === 'birthDate' &&
            (!dayjs(passenger.birthDate).isValid() ||
              dayjs(new Date()).diff(dayjs(passenger.birthDate).format('YYYY-MM-DD'), 'day') < 0)
          ) {
            passengersFormValidations[passenger.passengerIndex][key] = 'Enter valid birth date';
            isValid = false;
          }
          if (key === 'phone' && !validatePhoneNumberWithCountrySelect(passenger[key] || '')) {
            passengersFormValidations[passenger.passengerIndex][key] = 'Phone should be valid';
            isValid = false;
          }
          if (key === 'contactEmail' && !validateEmail(passenger[key])) {
            passengersFormValidations[passenger.passengerIndex][key] = 'Email should be valid';
            isValid = false;
          }

          // VALIDATE AGE BASED ON PASSENGER TYPE
          if (isValid && key === 'birthDate' && passenger.passengerType) {
            const isAnonymousBookingFromBrochure =
              typeof window !== 'undefined' &&
              (window.location?.search?.includes('brochure') ||
                window.location?.search?.includes('tripquote')) &&
              !isLoggedIn;
            const enabledTransportModes = isAnonymousBookingFromBrochure
              ? travelAgency?.agentPortalEnabledTransportModes ||
                travelAgency?.enabledTransportModes
              : travelAgency?.enabledTransportModes;
            const passengerTypes = GetPassengerTypes(enabledTransportModes);
            // GetPassengerTypes omits types the agency's enabled transport modes don't
            // support (e.g. seniors when cruise is disabled, infants when flight is
            // disabled), so a stale passengerType can miss the map entirely. Default to
            // an empty config: no fromAge/toAge means no age constraint, which is what
            // the guards below already expect.
            const passengerTypeConfig = passengerTypes?.[passenger.passengerType] || {};
            const passengerTypeLabel =
              PassengerTypes[passenger.passengerType]?.text || passenger.passengerType || 'Adult';
            if (allFlights?.length > 0) {
              const { fromAge, toAge, ignoreValidateToAge } = passengerTypeConfig;
              const passengerLabel = passengerTypeLabel.toLowerCase();
              const article = /^[aeiou]/i.test(passengerLabel) ? 'an' : 'a';

              // toAge is inclusive in the validation, so the exclusive bound is toAge + 1
              const maxText = toAge === 1 ? '24 months' : `${toAge + 1} years old`;
              let ageRequirement = '';

              // → "To travel as a child, the traveler must be at least 2 years old and under 12 years old…"
              // → "To travel as an infant on lap, the traveler must be under 24 months…"
              if (!ignoreValidateToAge && toAge && fromAge) {
                ageRequirement = `at least ${fromAge} years old and under ${maxText}`;
              } else if (fromAge) {
                ageRequirement = `at least ${fromAge} years old`;
              } else if (!ignoreValidateToAge && toAge) {
                ageRequirement = `under ${maxText}`;
              }

              allFlights.every((flight, flightIndex) => {
                const age = calculateAge(passenger.birthDate, flight.departDateTime);
                let errorMsg = '';
                let errorOnThisFlight = '';
                if (flightIndex > 0 && !passengersFormValidations[passenger.passengerIndex][key]) {
                  errorOnThisFlight = buildFlightMismatchMessage(passengerTypeLabel, flight);
                }
                if (fromAge && age < fromAge) {
                  isValid = false;
                  errorMsg =
                    `To travel as ${article} ${passengerLabel}, the traveler must be ${ageRequirement} for the entire trip. ${errorOnThisFlight}`.trim();
                }
                if (toAge && !ignoreValidateToAge && age > toAge) {
                  isValid = false;
                  errorMsg =
                    `To travel as ${article} ${passengerLabel}, the traveler must be ${ageRequirement} for the entire trip. ${errorOnThisFlight}`.trim();
                }
                if (errorMsg) passengersFormValidations[passenger.passengerIndex][key] = errorMsg;
                return true;
              });
            } else {
              const { fromAge, toAge, ignoreValidateToAge } = passengerTypeConfig;
              const age = calculateAge(passenger.birthDate);
              let errorMsg = '';
              if (fromAge && age < fromAge) {
                isValid = false;
                errorMsg = `The passenger must be at least ${fromAge} years old at the time of travel.`;
              }
              if (toAge && !ignoreValidateToAge && age > toAge) {
                isValid = false;
                errorMsg = `The passenger must be ${toAge} years old or younger at the time of travel.`;
              }
              if (errorMsg) passengersFormValidations[passenger.passengerIndex][key] = errorMsg;
            }
          }
        });

        const additionalConfirmationEmails = passenger.additionalConfirmationEmails || [];
        if (
          additionalConfirmationEmails.some(
            (email) => (email || '').trim() && !validateEmail((email || '').trim()),
          )
        ) {
          passengersFormValidations[passenger.passengerIndex].additionalConfirmationEmails =
            'One or more additional emails are invalid';
          isValid = false;
        }

        if (
          passenger.travelerPreferences.emergencyContact &&
          !passenger.doUseTravelerInfoAsEmergencyContact &&
          !passenger.doUsePrimaryTravelerAsEmergencyContact
        ) {
          if (!validateEmail(passenger.travelerPreferences.emergencyContact.contactEmail)) {
            passengersFormValidations[passenger.passengerIndex].emergencyContactContactEmail =
              'Email should be valid';
            isValid = false;
          }
          if (
            !validatePhoneNumberWithCountrySelect(
              passenger.travelerPreferences.emergencyContact.phoneNumber || '',
            )
          ) {
            passengersFormValidations[passenger.passengerIndex].emergencyContactPhoneNumber =
              'Phone should be valid';
            isValid = false;
          }
        }

        // Validation for seats
        if (
          !validatePassengerSeats(
            passenger,
            passengersByRoom,
            passengersFormValidations,
            flightSegments,
          )
        ) {
          isValid = false;
        }

        if (
          isInternationalFlight &&
          travelAgency &&
          travelAgency.settings &&
          travelAgency.settings.isPassportRequiredForInternationalFlights
        ) {
          const advancePassengerInfos =
            passenger &&
            passenger.travelerPreferences &&
            passenger.travelerPreferences.advancePassengerInformations &&
            passenger.travelerPreferences.advancePassengerInformations.length
              ? passenger.travelerPreferences.advancePassengerInformations
              : [];

          const selectAPISTypeCount = advancePassengerInfos.filter(
            (d) => d.apisType === SecurityInformationTypes.SELECT_APIS_TYPE,
          ).length;

          const hasSelectAPISType = selectAPISTypeCount === 1;
          const hasPassportDocument = advancePassengerInfos.some(
            (item) => item.apisType === SecurityInformationTypes.PASSPORT_DOCS,
          );
          const hasVisaDocument =
            advancePassengerInfos.length &&
            advancePassengerInfos.some(
              (item) => item.apisType === SecurityInformationTypes.VISA_DOCO,
            );

          if (!advancePassengerInfos.length || !hasPassportDocument) {
            if (hasSelectAPISType && !hasVisaDocument) {
              passengersFormValidations[passenger.passengerIndex].validatePassportErrorOnInput =
                'Passport is required, Please add a Passport as you are booking an international flight';
            } else if (!hasPassportDocument) {
              passengersFormValidations[passenger.passengerIndex].validatePassportErrorOnAddDoc =
                'Passport is required, Please add a Passport as you are booking an international flight';
            }
            isValid = false;
          }
        }

        if (
          passenger.travelerPreferences.advancePassengerInformations &&
          passenger.travelerPreferences.advancePassengerInformations.length
        ) {
          _.filter(
            passenger.travelerPreferences.advancePassengerInformations,
            (d) => d.apisType !== SecurityInformationTypes.SELECT_APIS_TYPE,
          ).forEach((item) => {
            const today = dayjs().startOf('day');

            if (!validateDocuments(item.documentNumber, 'documentNumber')) {
              const apisType =
                item.apisType === SecurityInformationTypes.PASSPORT_DOCS ? 'passport' : 'visa';
              passengersFormValidations[passenger.passengerIndex][`${apisType}DocumentNumber`] =
                `Valid ${apisType} document number is required`;
              isValid = false;
            }

            if (item.apisType === SecurityInformationTypes.PASSPORT_DOCS) {
              if (!validateDocuments(item.issuingCountryCode, 'issuingCountryCode')) {
                passengersFormValidations[passenger.passengerIndex].issuingCountryCode =
                  'Issuing Country is required';
                isValid = false;
              }
              if (!validateDocuments(item.nationalityCountryCode, 'nationalityCountryCode')) {
                passengersFormValidations[passenger.passengerIndex].nationalityCountryCode =
                  'Nationality is required for passport document';
                isValid = false;
              }
              if (item.expirationDate) {
                const selectedDate = dayjs(item.expirationDate).startOf('day');
                if (selectedDate.isBefore(today, 'day')) {
                  passengersFormValidations[passenger.passengerIndex].expirationDate =
                    "Expiration date can't be in the past";
                  isValid = false;
                }
              } else if (!validateDocuments(item.expirationDate, 'expirationDate')) {
                passengersFormValidations[passenger.passengerIndex].expirationDate =
                  'Expiration date is required';
                isValid = false;
              }
            } else if (item.apisType === SecurityInformationTypes.VISA_DOCO) {
              if (!validateDocuments(item.travelToCountryCode, 'travelToCountryCode')) {
                passengersFormValidations[passenger.passengerIndex].travelToCountryCode =
                  'Travel to country is required';
                isValid = false;
              }
              if (item.visaIssueDate) {
                const selectedDate = dayjs(item.visaIssueDate).startOf('day');
                if (selectedDate.isAfter(today, 'day')) {
                  passengersFormValidations[passenger.passengerIndex].visaIssueDate =
                    "Visa Issue date can't be in the future";
                  isValid = false;
                }
              }
              if (item.expirationDate) {
                const selectedDate = dayjs(item.expirationDate).startOf('day');
                if (selectedDate.isBefore(today, 'day')) {
                  passengersFormValidations[passenger.passengerIndex].visaExpirationDate =
                    "Expiration date can't be in the past";
                  isValid = false;
                }
              } else if (!validateDocuments(item.expirationDate, 'expirationDate')) {
                passengersFormValidations[passenger.passengerIndex].visaExpirationDate =
                  'Expiration date is required';
                isValid = false;
              }
            }
          });
        }
        if (!doIgnoreEmptyPassengerDetailValidation && doHoldOnlyCruiseAsOption) {
          /// Validate empty passenger details for holding cruise booking for checkout page
          if (isValid) {
            _.filter(
              Object.keys(passengerInfoValidations),
              (key) =>
                passengerInfoValidations[key] !== undefined && !passengerInfoValidations[key],
            ).forEach((key) => {
              // oxlint-disable-next-line no-empty
              if (index > 0 && (key === 'phone' || key === 'contactEmail')) {
              } else if (!passenger[key]) {
                // oxlint-disable-next-line no-empty
                if (index === 0 && ['postcode', 'address'].includes(key)) {
                } else if (!['postcode', 'address'].includes(key)) {
                  emptyPassengerDetailValidation = setEmptyFieldValidation(
                    emptyPassengerDetailValidation,
                    passenger.roomIndex,
                    index,
                    key,
                  );
                }
              }
            });
          } else emptyPassengerDetailValidation = {};
        }
      });
    },
  );

  const { isInvalid: hasDuplicateNames, errorMessage } = validatePassengerUniqueness(
    passengersArray,
    travelAgency,
    loggedInUserEmail,
  );

  if (hasDuplicateNames) {
    similarPassengersValidation = errorMessage || "Passengers can't be non-unique!";
    isValid = false;
  }

  if (
    !doIgnoreEmptyPassengerDetailValidation &&
    isValid &&
    tripConfirmation &&
    tripConfirmation.travelers &&
    tripConfirmation.travelers.length
  ) {
    /// Validate passenger details, when empty passengers input during holding cruise booking
    tripConfirmation.travelers.forEach((tcTraveler, index) => {
      const traveler = passengers[index];

      if (tcTraveler.fieldsEmptyDuringCheckout && tcTraveler.fieldsEmptyDuringCheckout.length > 0) {
        tcTraveler.fieldsEmptyDuringCheckout.forEach((fieldName) => {
          if (
            traveler[fieldName] &&
            tcTraveler[fieldName] &&
            tcTraveler[fieldName] === traveler[fieldName]
          ) {
            emptyPassengerDetailValidation = setEmptyFieldValidation(
              emptyPassengerDetailValidation,
              traveler.roomIndex,
              index,
              fieldName,
            );
          }
        });
      }
    });
  }

  return [
    isValid,
    passengersFormValidations,
    similarPassengersValidation,
    emptyPassengerDetailValidation,
  ];
};

export default validatePassengers;
